export type MessageLogStatus = 'queued' | 'delivered' | 'opened' | 'dropped'

export interface MessageLog {
  id: number
  email: string
  status: MessageLogStatus
  name: string
  created_at: string
}

export interface MessageReceiver {
  id: number
  receiver_group: ReceiverGroup
  in_words: string
}

export interface UploadedFile {
  id: number
  name: string
  url: string
  content_type: string
  file_size: number
}

export interface Message {
  id: number
  user_id: number
  group_id: number
  ruid: string
  subject: string
  body: string
  to_in_words: string
  copy_me: boolean
  sender_name: string
  sender_email: string
  percentage_opened: number
  created_at: string
  updated_at: string
  message_receivers: MessageReceiver[]
  message_logs: MessageLog[]
  uploaded_files?: UploadedFile[]
}

export type ReceiverGroup =
  | 'specific_admin'
  | 'investors'
  | 'group_admins'
  | 'companies'
  | 'specific_investor'
  | 'specific_fund'
  | 'specific_funding_entity'
  | 'chapters'
  | 'specific_chapter'
  | 'specific_company'
  | 'investments'
  | 'specific_investment'
  | 'specific_deal'
  | 'specific_deal_company'

export interface ExtraInfo {
  only_active?: boolean
  admins?: boolean
  investors?: boolean
  founders?: boolean
  committed_investors?: boolean
  followers?: boolean
  president?: boolean
  contact?: boolean
  specific_ids?: string[]
  [key: string]: string | string[] | boolean | undefined
}

export interface MessageReceiverAttribute {
  receiver_group: ReceiverGroup | ''
  extra_info: ExtraInfo
}

export interface CreateMessagePayload {
  subject: string
  body: string
  copy_me: boolean
  message_receivers_attributes: MessageReceiverAttribute[]
  files?: File[]
}

export interface RecipientPreview {
  count: number
  recipients: { email: string }[]
}

export interface EntityLookupItem {
  id: number | string
  name: string
}

export interface ReceiverGroupOption {
  label: string
  value: ReceiverGroup
  has_sub_filters: boolean
  has_specific_ids: boolean
}

export const RECEIVER_GROUP_OPTIONS: ReceiverGroupOption[] = [
  { label: 'All Investors', value: 'investors', has_sub_filters: true, has_specific_ids: false },
  { label: 'All Group Admins', value: 'group_admins', has_sub_filters: false, has_specific_ids: false },
  { label: 'All Companies', value: 'companies', has_sub_filters: true, has_specific_ids: false },
  { label: 'All Chapters', value: 'chapters', has_sub_filters: true, has_specific_ids: false },
  { label: 'All Investments', value: 'investments', has_sub_filters: true, has_specific_ids: false },
  { label: 'Specific Admins', value: 'specific_admin', has_sub_filters: false, has_specific_ids: true },
  { label: 'Specific Investors', value: 'specific_investor', has_sub_filters: false, has_specific_ids: true },
  { label: 'Specific Funds', value: 'specific_fund', has_sub_filters: false, has_specific_ids: true },
  {
    label: 'Specific Funding Entities',
    value: 'specific_funding_entity',
    has_sub_filters: false,
    has_specific_ids: true,
  },
  { label: 'Specific Chapters', value: 'specific_chapter', has_sub_filters: false, has_specific_ids: true },
  { label: 'Specific Companies', value: 'specific_company', has_sub_filters: false, has_specific_ids: true },
  { label: 'Specific Investments', value: 'specific_investment', has_sub_filters: false, has_specific_ids: true },
  { label: 'Specific Deals', value: 'specific_deal', has_sub_filters: false, has_specific_ids: true },
]

export const SUB_FILTER_KEYS = [
  { key: 'only_active', label: 'Only active' },
  { key: 'admins', label: 'Admins' },
  { key: 'investors', label: 'Investors' },
  { key: 'founders', label: 'Founders' },
  { key: 'committed_investors', label: 'Committed investors' },
  { key: 'followers', label: 'Followers' },
  { key: 'president', label: 'President' },
  { key: 'contact', label: 'Contact' },
] as const
